import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiError, resolveImageUrl } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";

const CITIES = ["Indore", "Goa"];
const TYPES = ["Residential", "Commercial", "Plot", "Airbnb"];
const LISTING = ["buy", "rent"];

const empty = () => ({
  title_en: "",
  title_hi: "",
  description_en: "",
  description_hi: "",
  city: "Indore",
  locality: "",
  property_type: "Residential",
  listing_type: "buy",
  price: 0,
  price_display: "",
  bhk: "",
  sqft: "",
  furnishing: "",
  facing: "",
  ownership: "",
  is_exclusive: false,
  is_assured_return: false,
  assured_return_pct: "",
  assured_rental_monthly: "",
  lat: "",
  lng: "",
  is_featured: true,
  image_paths: [],
  amenities: [],
});

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const [properties, setProperties] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [editing, setEditing] = useState(null); // null | {}
  const [form, setForm] = useState(empty());
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") refresh();
  }, [user]);

  async function refresh() {
    setRefreshing(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/properties"),
        api.get("/enquiries"),
      ]);
      setProperties(a.data || []);
      setEnquiries(b.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setRefreshing(false);
    }
  }

  function openCreate() {
    setForm(empty());
    setEditing({});
  }

  function openEdit(p) {
    setForm({
      ...empty(),
      ...p,
      bhk: p.bhk ?? "",
      sqft: p.sqft ?? "",
      assured_return_pct: p.assured_return_pct ?? "",
      assured_rental_monthly: p.assured_rental_monthly ?? "",
      lat: p.lat ?? "",
      lng: p.lng ?? "",
      amenities: p.amenities || [],
      image_paths: p.image_paths || [],
    });
    setEditing(p);
  }

  async function save() {
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        bhk: form.bhk === "" ? null : Number(form.bhk),
        sqft: form.sqft === "" ? null : Number(form.sqft),
        assured_return_pct:
          form.assured_return_pct === "" ? null : Number(form.assured_return_pct),
        assured_rental_monthly:
          form.assured_rental_monthly === "" ? null : Number(form.assured_rental_monthly),
        lat: form.lat === "" ? null : Number(form.lat),
        lng: form.lng === "" ? null : Number(form.lng),
        amenities: Array.isArray(form.amenities)
          ? form.amenities
          : String(form.amenities || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
      };
      if (editing?.id) {
        const { data } = await api.put(`/properties/${editing.id}`, payload);
        toast.success("Property updated");
        setProperties((prev) => prev.map((p) => (p.id === data.id ? data : p)));
      } else {
        const { data } = await api.post("/properties", payload);
        toast.success("Property added");
        setProperties((prev) => [data, ...prev]);
      }
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function remove(id) {
    try {
      await api.delete(`/properties/${id}`);
      setProperties((prev) => prev.filter((p) => p.id !== id));
      toast.success("Property deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteId(null);
    }
  }

  async function uploadFiles(files) {
    const added = [];
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        added.push(data.path);
      }
      setForm((f) => ({ ...f, image_paths: [...f.image_paths, ...added] }));
      toast.success(`${added.length} image(s) uploaded`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  }

  const amenitiesText = useMemo(
    () =>
      Array.isArray(form.amenities)
        ? form.amenities.join(", ")
        : form.amenities || "",
    [form.amenities]
  );

  if (loading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#F4F7FB] text-[#0B2545] font-sans">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-semibold">
              {t.nav.dashboard}
            </div>
            <h1 className="mt-2 font-serif text-3xl md:text-4xl text-[#0B2545] rc-heading">
              {t.admin.dashboard}
            </h1>
          </div>
          <Button
            onClick={openCreate}
            data-testid="admin-add-property-btn"
            className="bg-[#0B2545] text-[#C9A84C] hover:bg-[#133763] hover:text-[#E9D58F] font-semibold tracking-wider uppercase text-xs"
          >
            <Plus size={16} className="mr-2" />
            {t.admin.add}
          </Button>
        </div>

        <Tabs defaultValue="listings" className="mt-10">
          <TabsList className="bg-white border border-[#EBEFF5]">
            <TabsTrigger value="listings" data-testid="tab-listings">
              {t.admin.listings} ({properties.length})
            </TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">
              {t.admin.enquiries} ({enquiries.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="listings" className="mt-6">
            <div className="bg-white rounded-xl border border-[#EBEFF5] overflow-hidden">
              <Table data-testid="admin-listings-table">
                <TableHeader>
                  <TableRow className="bg-[#F4F7FB]">
                    <TableHead>Title</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Flags</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {properties.map((p) => (
                    <TableRow key={p.id} data-testid={`admin-row-${p.id}`}>
                      <TableCell className="font-medium">{p.title_en}</TableCell>
                      <TableCell>{p.city}</TableCell>
                      <TableCell>{p.property_type}</TableCell>
                      <TableCell>{p.price_display || `₹${p.price}`}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {p.is_exclusive && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] bg-[#0B2545] text-[#C9A84C]">
                              Exclusive
                            </span>
                          )}
                          {p.is_assured_return && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] bg-[#FEF9C3] text-[#854D0E]">
                              {p.assured_return_pct || ""}% Assured
                            </span>
                          )}
                          {p.is_featured && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] bg-[#EEF2FF] text-[#3730A3]">
                              Featured
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEdit(p)}
                          data-testid={`admin-edit-${p.id}`}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteId(p.id)}
                          data-testid={`admin-delete-${p.id}`}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {properties.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-[#1F2A44] py-10">
                        No properties yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="enquiries" className="mt-6">
            <div className="bg-white rounded-xl border border-[#EBEFF5] overflow-hidden">
              <Table data-testid="admin-enquiries-table">
                <TableHeader>
                  <TableRow className="bg-[#F4F7FB]">
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Received</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enquiries.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{e.name}</TableCell>
                      <TableCell>
                        <a
                          href={`tel:${e.phone}`}
                          className="text-[#0B2545] hover:text-[#C9A84C]"
                        >
                          {e.phone}
                        </a>
                      </TableCell>
                      <TableCell>{e.city || "—"}</TableCell>
                      <TableCell>{e.property_title || "General"}</TableCell>
                      <TableCell className="max-w-xs truncate">
                        {e.message || "—"}
                      </TableCell>
                      <TableCell className="text-xs text-[#1F2A44]">
                        {new Date(e.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                  {enquiries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-[#1F2A44] py-10">
                        {t.admin.no_enquiries}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />

      {/* Property form dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent
          className="sm:max-w-3xl max-h-[90vh] overflow-y-auto bg-white"
          data-testid="admin-property-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#0B2545]">
              {editing?.id ? t.admin.edit : t.admin.add}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                {t.admin.title_en}
              </Label>
              <Input
                data-testid="admin-form-title-en"
                value={form.title_en}
                onChange={(e) => setForm({ ...form, title_en: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                {t.admin.title_hi}
              </Label>
              <Input
                data-testid="admin-form-title-hi"
                value={form.title_hi}
                onChange={(e) => setForm({ ...form, title_hi: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                {t.admin.description_en}
              </Label>
              <Textarea
                data-testid="admin-form-desc-en"
                rows={3}
                value={form.description_en}
                onChange={(e) =>
                  setForm({ ...form, description_en: e.target.value })
                }
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                {t.admin.description_hi}
              </Label>
              <Textarea
                data-testid="admin-form-desc-hi"
                rows={3}
                value={form.description_hi}
                onChange={(e) =>
                  setForm({ ...form, description_hi: e.target.value })
                }
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                City
              </Label>
              <Select
                value={form.city}
                onValueChange={(v) => setForm({ ...form, city: v })}
              >
                <SelectTrigger data-testid="admin-form-city"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CITIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                {t.admin.locality}
              </Label>
              <Input
                data-testid="admin-form-locality"
                value={form.locality}
                onChange={(e) => setForm({ ...form, locality: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                Property Type
              </Label>
              <Select
                value={form.property_type}
                onValueChange={(v) => setForm({ ...form, property_type: v })}
              >
                <SelectTrigger data-testid="admin-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                Listing Type
              </Label>
              <Select
                value={form.listing_type}
                onValueChange={(v) => setForm({ ...form, listing_type: v })}
              >
                <SelectTrigger data-testid="admin-form-listing"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LISTING.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c === "buy" ? "Buy" : "Rent"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                {t.admin.price}
              </Label>
              <Input
                data-testid="admin-form-price"
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                {t.admin.price_display}
              </Label>
              <Input
                data-testid="admin-form-price-display"
                value={form.price_display}
                onChange={(e) => setForm({ ...form, price_display: e.target.value })}
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">BHK</Label>
              <Input
                data-testid="admin-form-bhk"
                type="number"
                value={form.bhk}
                onChange={(e) => setForm({ ...form, bhk: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">Sqft</Label>
              <Input
                data-testid="admin-form-sqft"
                type="number"
                value={form.sqft}
                onChange={(e) => setForm({ ...form, sqft: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                Furnishing
              </Label>
              <Input
                data-testid="admin-form-furnishing"
                value={form.furnishing}
                onChange={(e) => setForm({ ...form, furnishing: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">Facing</Label>
              <Input
                data-testid="admin-form-facing"
                value={form.facing}
                onChange={(e) => setForm({ ...form, facing: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                Ownership
              </Label>
              <Input
                data-testid="admin-form-ownership"
                value={form.ownership}
                onChange={(e) => setForm({ ...form, ownership: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                {t.admin.assured_pct}
              </Label>
              <Input
                data-testid="admin-form-assured-pct"
                type="number"
                step="0.1"
                value={form.assured_return_pct}
                onChange={(e) =>
                  setForm({ ...form, assured_return_pct: e.target.value })
                }
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                Assured Rental (₹ / month)
              </Label>
              <Input
                data-testid="admin-form-assured-rental"
                type="number"
                placeholder="e.g. 55000"
                value={form.assured_rental_monthly}
                onChange={(e) =>
                  setForm({ ...form, assured_rental_monthly: e.target.value })
                }
              />
            </div>
            {["lat", "lng"].map((k) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                  {k === "lat" ? "Map Latitude" : "Map Longitude"}
                </Label>
                <Input
                  data-testid={`admin-form-${k}`}
                  type="number"
                  step="0.0001"
                  placeholder={k === "lat" ? "e.g. 15.5522" : "e.g. 73.8011"}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                />
              </div>
            ))}

            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                {t.admin.amenities}
              </Label>
              <Input
                data-testid="admin-form-amenities"
                value={amenitiesText}
                onChange={(e) =>
                  setForm({ ...form, amenities: e.target.value.split(",").map((s) => s.trim()) })
                }
                placeholder="Clubhouse, Swimming Pool, Lift"
              />
            </div>

            {/* Flags */}
            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 rounded-lg bg-[#F4F7FB] border border-[#EBEFF5]">
              <label className="flex items-center justify-between gap-3">
                <span className="text-sm text-[#0B2545]">{t.admin.exclusive}</span>
                <Switch
                  checked={form.is_exclusive}
                  onCheckedChange={(v) => setForm({ ...form, is_exclusive: v })}
                  data-testid="admin-form-exclusive"
                />
              </label>
              <label className="flex items-center justify-between gap-3">
                <span className="text-sm text-[#0B2545]">{t.admin.assured}</span>
                <Switch
                  checked={form.is_assured_return}
                  onCheckedChange={(v) => setForm({ ...form, is_assured_return: v })}
                  data-testid="admin-form-assured"
                />
              </label>
              <label className="flex items-center justify-between gap-3">
                <span className="text-sm text-[#0B2545]">{t.admin.featured}</span>
                <Switch
                  checked={form.is_featured}
                  onCheckedChange={(v) => setForm({ ...form, is_featured: v })}
                  data-testid="admin-form-featured"
                />
              </label>
            </div>

            {/* Image upload */}
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#1F2A44]">
                {t.admin.upload}
              </Label>
              <div className="mt-2 flex items-center gap-3">
                <label
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#C9A84C] text-[#0B2545] bg-[#FFFDF5] hover:bg-[#F6EFD6] cursor-pointer text-sm font-semibold"
                  data-testid="admin-form-upload-btn"
                >
                  <Upload size={16} />
                  {uploading ? "Uploading…" : "Choose files"}
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files?.length) uploadFiles(Array.from(e.target.files));
                      e.target.value = "";
                    }}
                  />
                </label>
                <span className="text-xs text-[#1F2A44]">
                  Or paste image URLs below (comma separated)
                </span>
              </div>
              <Input
                className="mt-3"
                data-testid="admin-form-image-urls"
                value={form.image_paths.join(", ")}
                onChange={(e) =>
                  setForm({
                    ...form,
                    image_paths: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
                placeholder="https://..."
              />
              {form.image_paths.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {form.image_paths.map((p, i) => (
                    <div
                      key={i}
                      className="relative w-20 h-20 rounded-md overflow-hidden border border-[#EBEFF5] bg-[#F4F7FB]"
                    >
                      <img
                        src={resolveImageUrl(p)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setForm({
                            ...form,
                            image_paths: form.image_paths.filter((_, idx) => idx !== i),
                          })
                        }
                        className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              {t.admin.cancel}
            </Button>
            <Button
              onClick={save}
              data-testid="admin-add-property-submit"
              className="bg-[#0B2545] text-[#C9A84C] hover:bg-[#133763] font-semibold"
            >
              {t.admin.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.admin.confirm_delete}</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">
              {t.admin.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove(deleteId)}
              data-testid="admin-delete-confirm"
              className="bg-red-600 hover:bg-red-700"
            >
              {t.admin.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
